<template>
    <div class="full" :class="this.wrapperClass">
        <div class="row">
            <div v-if="memberFirst"  class="col-12">
                <div class="field-label" v-if="showFieldLabel">{{provider.landing_member_label || $t('provider.member_user')}}</div>
                <ding-select
                    cy-member-select
                    ref="memberSelect"
                    v-model="selectedMember"
                    :options="aliveMembers"
                    :placeholder="provider.landing_member_label ? $t('message.select_custom', {custom: provider.landing_member_label}) : $t('message.select_member')"
                    value-attribute="id"
                    :preselectFirst="aliveMembers.length === 1"
                    label-attribute="name">
                </ding-select>
            </div>
            <div v-else class="col-12">
                <service-select
                    cy-service-select
                    ref="serviceSelect"
                    :showFieldLabel="showFieldLabel"
                    v-model="selectedService"
                    :provider="provider"
                    :preselectFirst="false"
                    :options="serviceOptions"></service-select>
            </div>
        </div>
        <div v-if="memberFirst" class="row mt-3">
            <div class="col-12">
                <service-select
                    ref="serviceSelect"
                    :showFieldLabel="showFieldLabel"
                    v-model="selectedService"
                    :provider="provider"
                    :preselectFirst="false"
                    :options="serviceOptionsForCurrentMember"></service-select>
            </div>
        </div>
        <div class="row">
            <div v-if="!memberFirst && !compact" class="col-sm col-12 mt-3">
                <div class="field-label" v-if="showFieldLabel">{{provider.landing_member_label || $t('provider.member_user')}}</div>
                <ding-select
                    ref="memberSelect"
                    v-model="selectedMember"
                    :options="memberForCurrentServices"
                    :placeholder="provider.landing_member_label ? $t('message.select_custom', {custom: provider.landing_member_label}) : $t('message.select_member')"
                    :preselectFirst="memberForCurrentServices.length > 0"
                    :disabled="!selectedService"
                    value-attribute="id"
                    label-attribute="name">
                </ding-select>
            </div>
            <div v-if="!compact" class="col-sm col-12 mt-2 mt-sm-3">
                <div class="field-label" v-if="showFieldLabel">{{$t('provider.service_datetime')}}</div>
                <available-date-time-picker
                    :check-cart-items="true"
                    ref="availableDateTimePicker"
                    :overwritePlaceholder="startDateTimePickerPlaceholder"
                    :disabled="!selectedService || !selectedMember"
                    input-class="availability-calendar-input-class"
                    v-model="selectedDate"
                    :service="selectedService"
                    :member="selectedMember"
                    :provider="provider"
                    :fixed-time="selectedService ? selectedService.start_time : null"
                    :context="context" />
            </div>
            <div 
                v-if="selectedService && selectedService.allow_custom_duration && !compact"
                class="col-sm col-12 mt-2 mt-sm-3">
                <div class="field-label" v-if="showFieldLabel">{{$t('provider.service_datetime')}}</div>
                <available-date-time-picker
                    :extraAvailabilityCheck="isTimeslotAvailableForEndDate"
                    :check-cart-items="true"
                    :overwritePlaceholder="$t('message.select_custom', {custom: (provider.landing_choose_end_time_label || $t('general.datetime_to_booking'))})"
                    :ignore-service-start-time="true"
                    ref="availableEndDateTimePicker"
                    :disabled="!selectedService || !selectedMember"
                    input-class="availability-calendar-input-class"
                    v-model="selectedEndDate"
                    :cross-over-disabled="true"
                    :start-moment="selectedDateMoment"
                    :fixed-time="selectedService.custom_duration_fixed_endtime"
                    :service="selectedService"
                    :member="selectedMember"
                    :provider="provider"
                    :context="context" />
            </div>
            <div v-if="!compact" class="col-sm col-12 mt-3">
                <div class="field-label" v-if="showFieldLabel">&nbsp;</div>
                <button
                    :disabled="!this.selectedDate"
                    class="btn btn-primary btn-block theme-tinted-background theme-tinted-border btn-check"
                    @click="redirectToBooking"
                >{{ provider.landing_book_label || $t('message.book') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import moment from 'moment';
import 'moment-timezone';
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import loadingIndicator from '../common/LoadingIndicator';
import dingSelect from '../common/Select';
import availableDateTimePicker from './AvailableDateTimePicker';
import fullOverlay from '../common/FullOverlay';
import _ from 'lodash';
import serviceSelect from '../landing/TimeCheckingServiceSelect';
import { EventSelectMember, EventSelectService } from '../../bu/models/GA4';
import { mapGetters } from 'vuex';
import { getExchangedAmount } from '../../bu/CurrencyUtils';

export default {
    components: { fullOverlay, Datepicker, loadingIndicator, dingSelect, availableDateTimePicker, serviceSelect },
    mixins: [api, promptConfirm],
    props: {
        wrapperClass: String,
        compact: { type: Boolean, default: false },
        provider: {
            type: Object,
            required: true
        },
        context: { type: Object, require: true},
        showFieldLabel: {
            type: Boolean,
            default: false
        },
        openBookingInBlankTab: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        startDateTimePickerPlaceholder() {
            if (this.selectedService?.allow_custom_duration) {
                if (this.provider.landing_choose_time_label) {
                    return this.$t('message.select_custom', {custom: this.provider.landing_choose_time_label});
                } else {
                    return this.$t('message.select_start_time');
                }
            } else {
                if (this.provider.landing_choose_time_label) {
                    return this.$t('message.select_custom', {custom: this.provider.landing_choose_time_label});
                } else {
                    return null;
                }
            }
        },
        shouldBookButtonEnabled() {
            return this.selectedService
                && this.selectedService.start_time
                && (!this.selectedService.allow_custom_duration || this.selectedEndDate)
        },
        selectedDateMoment() {
            if (this.selectedDate) {
                return moment(this.selectedDate);
            }
            return null;
        },
        aliveMembers() {
            return this.provider.members.filter((u) => {
                return !u.pivot.deleted_at && u.services.length > 0 && !u.pivot.is_internal;
            })
        },
        serviceOptions() {
            return this.provider.services.filter((s) => {
                if (s.archived_at) {
                    return false;
                }

                if (s.is_disabled && !s.is_preview) {
                    return false;
                }

                if (s.is_private) {
                    return false;
                }

                let membersForService = this.provider.members.filter((member) => {
                    if (member.pivot.deleted_at || member.pivot.is_internal) {
                        return false;
                    }
                    return member.services.filter((memberService) => {
                        return memberService.id === s.id;
                    }).length > 0;
                });

                if (membersForService.length === 0) {
                    return false;
                }

                if (s.limited_periods && s.limited_periods.length > 0) {
                    var now = moment();
                    var validPeriods = s.limited_periods.filter((p) => {
                        return moment.tz(p.end, 'GMT').isAfter(now);
                    });
                    if (validPeriods.length === 0) {
                        return false;
                    }
                }

                return true;
            }).map((s) => {
                var title = s.title;
                var price = '';
                switch (s.price_policy) {
                    case 'min':
                        price = [this.getExchangedAmount(s.price_min), this.$t('services.price_up')].join(' ');
                        break;
                    case 'fixed':
                        price = this.getExchangedAmount(s.price_min);
                        break;
                    case 'range':
                        price = [
                            this.getExchangedAmount(s.price_min),
                            this.$t('general.to'),
                            this.getExchangedAmount(s.price_max),
                        ].join(' ');
                        break;
                    default:
                        break;
                }
                var ret = s;
                ret.value = s.id;
                ret.price = price;
                return ret;
            }).sort((a, b) => {
                return a.order - b.order;
            });
        },
        serviceOptionsForCurrentMember() {
            var serviceOptions = [];

            if (this.selectedMember) {
                let members = this.provider.members.filter((item) => { return item.id === this.selectedMember.id});
                if (members.length) {
                    var member = members[0];
                    let memberAvailableServiceIds = member.services.map((item) => { return item.id; });
                    serviceOptions = this.serviceOptions.filter((service) => {
                        return memberAvailableServiceIds.indexOf(service.id) >= 0;
                    });
                }
            }

            return serviceOptions;
        },
        memberForCurrentServices() {
            this.selectedMember = null;

            let members = this.provider.members.filter((u) => {
                if (u.pivot.deleted_at || u.pivot.is_internal) {
                    return false;
                }
                for (var i in u.services) {
                    const service = u.services[i];
                    if (this.selectedService && service.id === this.selectedService.id) {
                        return true;
                    }
                }
                return false;
            });

            if (members.length) {
                this.selectedMember = members[0];
                if (this.$refs.availableDateTimePicker) {
                    this.$refs.availableDateTimePicker.clear();
                }
            }

            return members.sort((m1, m2) => {
                return m1.pivot.order - m2.pivot.order;
            }).map((m) => {
                return {
                    id: m.id,
                    name: m.name,
                }
            });
        },

        dateDisabled: function() {
            return {
                customPredictor: (date) => {
                    date = moment(date).startOf('day').toDate();
                    var today = moment().startOf('day').toDate();
                    if (date < today) {
                        return true;
                    }

                    if (this.selectedService && this.selectedService.limited_periods && this.selectedService.limited_periods.length > 0) {
                        var validRanges = this.selectedService.limited_periods.filter((p) => {
                            var range = moment.range(
                                moment.tz(p.start, 'GMT').toDate(),
                                moment.tz(p.end, 'GMT').toDate()
                            );
                            return moment(date).within(range);
                        });
                        return validRanges.length === 0;
                    }

                    return false;
                }
            };
        }
    },

    data() {
        return {
            selectedDate: null,
            selectedEndDate: null,
            selectedService: null,
            selectedMember: null,
            availabilities: null,
            memberFirst: this.provider.ask_order === 'ASK_ORDER_MEMBER_FIRST'
        }
    },

    watch: {
        selectedService() {
            this.$emit('service-selected', this.selectedService);
            this.trackSelectService();
            if (this.selectedService) {
                if (this.selectedService.limited_periods.length === 1) {
                    const limitedPeriod = this.selectedService.limited_periods[0];
                    const periodStart = moment.tz(limitedPeriod.start, 'GMT').tz(this.provider.timezone);
                    const periodEnd = moment.tz(limitedPeriod.end, 'GMT').tz(this.provider.timezone);
                    if (periodStart.isSame(periodEnd, 'day') && this.selectedService.start_time) {
                        const components = this.selectedService.start_time.split(':');
                        const targetMoment = moment(periodStart).hour(components[0]).minute(components[1]);
                        this.selectedDate = new Date(targetMoment.format());
                    }
                }
            }
        },
        selectedMember() {
            this.$emit('member-selected', this.selectedMember);
            this.trackSelectMember();
            if (this.memberFirst) {
                this.selectedService = null;
            }
        },
        selectedDateMoment() {
            this.$refs['availableEndDateTimePicker']?.clear()
        },
    },

    methods: {
        isTimeslotAvailableForEndDate(timeSlotMoment) {
            // 1. Cannot be earlier than selected start date time
            if (this.selectedDateMoment.isAfter(timeSlotMoment) || this.selectedDateMoment.isSame(timeSlotMoment)) {
                return false;
            }
            
            // 2. Cannot not be later than selected start date time + longest day range
            if (this.selectedService.maximum_custom_duration_value > 0) {
                if (this.selectedService.maximum_custom_duration_unit === 'night') {
                    const refrenceMoment = this.selectedDateMoment.clone().startOf('day');
                    const nights = Math.ceil(timeSlotMoment.diff(refrenceMoment, 'days', true)) - 1;
                    if (nights > this.selectedService.minimum_custom_duration_value) {
                        return false;
                    }
                } else if (this.selectedService.maximum_custom_duration_unit === 'day') {
                    const refrenceMoment = this.selectedDateMoment.clone().startOf('day');
                    const days = Math.ceil(timeSlotMoment.diff(refrenceMoment, 'days', true));
                    if (days > this.selectedService.maximum_custom_duration_value) {
                        return false;
                    }
                } else {
                    const duration = timeSlotMoment.diff(this.selectedDateMoment, this.selectedService.maximum_custom_duration_unit, true);
                    if (duration > this.selectedService.maximum_custom_duration_value) {
                        return false;
                    }
                }
            } 

            // 3. Duration cannot be shorter than minimum duration set
            if (this.selectedService.minimum_custom_duration_unit) {
                if (this.selectedService.minimum_custom_duration_unit === 'night') {
                    const refrenceMoment = this.selectedDateMoment.clone().startOf('day');
                    const nights = Math.ceil(timeSlotMoment.diff(refrenceMoment, 'days', true)) - 1;
                    if (nights < this.selectedService.minimum_custom_duration_value) {
                        return false;
                    }
                }  else if (this.selectedService.minimum_custom_duration_unit === 'day') {
                    const refrenceMoment = this.selectedDateMoment.clone().startOf('day');
                    const days = Math.ceil(timeSlotMoment.diff(refrenceMoment, 'days', true));
                    if (days < this.selectedService.minimum_custom_duration_value) {
                        return false;
                    }
                } else {
                    const duration = timeSlotMoment.diff(this.selectedDateMoment, this.selectedService.minimum_custom_duration_unit, true);
                    if (duration < this.selectedService.minimum_custom_duration_value) {
                        return false;
                    }
                }
            }

            const dateTimePicker = this.$refs['availableEndDateTimePicker'].$el ? this.$refs['availableEndDateTimePicker'] : this.$refs['availableEndDateTimePickerMobile'];
            if (dateTimePicker) {
                const bookingManager = dateTimePicker.bookingManager;
                const startMoment = moment(this.selectedDate).format('YYYY-MM-DD HH:mm:ss');
                if (this.selectedService && !bookingManager.isTimeslotCanBeEndTimeslot(this.selectedService, startMoment, timeSlotMoment)) {
                    return false;
                };
            }
            return true;
        },
        redirectToBooking() {
            const $form = $('<form class="d-none"></form>');
            const start = moment(this.selectedDate).format('YYYY-MM-DD HH:mm:ss');
            $form.attr('method', 'get');
            $form.attr('action', '/book');
            if (this.openBookingInBlankTab) {
                $form.attr('target', '_blank');
            }
            $form.append($('<input name="member_user_id" />').val(this.selectedMember.id));
            $form.append($('<input name="service_id" />').val(this.selectedService.id));
            $form.append($('<input name="date" />').val(encodeURIComponent(start)));
            if (this.selectedService.allow_custom_duration && !this.selectedEndDate) {
                const message = this.provider.landing_choose_end_time_label ? this.$t('message.please_select_custom', {custom: this.provider.landing_choose_end_time_label}) : this.$t('message.please_select_end_date_first');
                this.promptAlert(message, () => {
                    this.$refs['availableEndDateTimePicker'].open();
                });
                return;
            }
            if (this.selectedEndDate) {
                const end = moment(this.selectedEndDate).format('YYYY-MM-DD HH:mm:ss');
                $form.append($('<input name="end_date" />').val(encodeURIComponent(end)));
            }
            $('body').append($form);
            $form.submit();
        },
        trackSelectService() {
            window.tracker.track(() => new EventSelectService(this.selectedService));
        },
        trackSelectMember() {
            window.tracker.track(() => new EventSelectMember(this.selectedMember));
        },
        getExchangedAmount(amount) {
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.currency,
                currencyOptions: this.currencyOptions,
            });
        },
    }
}
</script>

<style lang="scss" scoped>
    .time-checking-component {
        position: relative;
        width: 90%;
        margin: 0 auto;
    }

    @media (min-width: 768px) {
        .time-checking-component {
            width: 66%;
        }
    }
    @media (min-width: 800px) {
        .time-checking-component {
            width: 64%;
        }
    }

    /deep/ .field-label, .field-label {
        font-size: 12px;
        font-weight: bold;
        color: white;
        margin-bottom: 4px;
    }

    .full /deep/ .vdp-datepicker {
        .datepicker-container {
            top: 48px;
            border: none !important;
            box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.13);
        }


        .datepicker-container header .next::after {
            border-left: 10px solid white !important;
        }

        .datepicker-container header .prev::after {
            border-right: 10px solid white !important;
        }

        .datepicker-input {
            background-color: white !important;
            padding: 6px 0;
            font-size: 16px;
            color: #666 !important;
            border-radius: 4px;
            cursor: pointer;
            width: 100%;
            text-align: center;
            border: none;
        }

        .spinner-container {
            position: fixed;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            background: rgba(240, 240, 240, 0.8);
        }

        .date-time-selector {
            display: flex;
            flex-direction: column;
        }

        .date-time-selector > div {

        }

        .date-time-selector button {
            width: 100%;
        }

        .vdp-datepicker__calendar .cell.selected, .vdp-datepicker__calendar .cell.selected.highlighted, .vdp-datepicker__calendar .cell.selected:hover {
            background: lightgrey !important;
        }

        .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover, .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover, .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
            border: 1px solid lightgrey !important;
        }

    }

    .full /deep/ .availability-calendar-input-class {
        height: 34px;
        text-align: center;
        font-size: 16px !important;
        min-width: auto !important;
    }

    .btn-check {
        height: 34px;
        font-size: 15px !important;
    }

    @media screen and (max-width: 576px) {
        /deep/ .availability-calendar-input-class, /deep/ .availability-calendar-input-class + .placeholder {
            padding-left: 1.25rem;
            padding-right: 32px;
        }
    }
</style>
